// The identifier that lives in the address, named as one.
//
// A UUID on the rail read as a second title and as a mystery string above
// Account. Dashboard and Show details are the pages that have to say which
// activation this is, so the sentence lives here rather than being restated
// on each. Show details and the dashboard's reading state use the whole
// component; the dashboard's ready state needs the words in a metadata band
// and takes ACTIVATION_UUID_LABEL on its own.

import type { ReactNode } from "react";
import { Info } from "@nova/desk-ui/kit";

/**
 * What this identifier is called, wherever it is drawn.
 *
 * Exported because the dashboard cannot use the component. Its ready state puts
 * the UUID in a metadata band beside Read at and Access — a `<dl>` of three
 * facts, not a standalone panel — so it needs the words without the shape. It
 * had its own copy of them until this constant existed, which is two places to
 * change and one of them gets forgotten.
 */
export const ACTIVATION_UUID_LABEL = "Activation ID";

export type ActivationUuidProps = {
  readonly id: string;
  readonly help?: string;
};

export function ActivationUuid({ id, help }: ActivationUuidProps): ReactNode {
  // .fixed is the shared block for a fact that is stated rather than edited: a
  // panel with no control in it, the value set in the mono face so the groups of
  // a UUID stay countable, and word-break so a 36-character identifier wraps
  // instead of widening the page. Drawn the same way on both desks, which is why
  // it is furniture rather than a rule in console.css.
  //
  // The help is an (i) in the label since the October hand-off, rather than a
  // line under the value. The block is a div with no control in it, so the
  // (i) can sit in its label without taking anything's name.
  return (
    <div className="fixed console-activation-uuid">
      <span className="label">
        {ACTIVATION_UUID_LABEL}
        {help === undefined ? null : <Info label="About the activation ID">{help}</Info>}
      </span>
      <p className="val">{id}</p>
    </div>
  );
}
